import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";
import DevelopmentPdfPreview from "../components/DevelopmentPdfPreview";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentDocuments.css";

const AGREEMENT_DEFAULTS = {
  effective_date: new Date()
    .toISOString()
    .slice(0, 10),
  project_scope:
    "Avwakin Development will provide the website development services and deliverables described in the linked proposal, approved project records, and any later written change requests approved by both parties.",
  payment_terms:
    "The client agrees to pay the amounts shown in the linked proposal and invoices according to the approved payment schedule.",
  deposit_terms:
    "A 30% deposit is required before project work begins. The remaining balance is due according to the approved project payment schedule.",
  revision_allowance:
    "Two reasonable revision rounds are included unless the linked proposal states otherwise. Additional revisions may require a written change request and additional fees.",
  change_request_terms:
    "Work outside the approved scope must be requested in writing. Avwakin Development will provide any resulting price or timeline adjustment before beginning the additional work.",
  client_responsibilities:
    "The client will provide timely access to content, branding assets, credentials, approvals, and other materials needed to complete the project. Delays in receiving these items may extend the project timeline.",
  project_timeline:
    "The project timeline begins after the deposit is received and all required client materials are available. Estimated dates may change when scope, approvals, or required materials are delayed.",
  ownership_terms:
    "Upon full payment, the client will own the final custom website content and deliverables created specifically for the project. Avwakin Development retains ownership of preexisting tools, reusable code, processes, and third-party materials.",
  third_party_services:
    "Third-party services, software, hosting, domains, payment processors, plugins, subscriptions, and similar services may be governed by separate terms and fees charged by their providers.",
  hosting_maintenance:
    "Hosting, maintenance, updates, monitoring, and ongoing support are included only when specifically listed in the linked proposal or a separate written service agreement.",
  cancellation_terms:
    "Either party may cancel the project by written notice. The client remains responsible for completed work, approved expenses, and nonrefundable third-party costs incurred before cancellation.",
  liability_terms:
    "Avwakin Development will perform the services with reasonable care but does not guarantee uninterrupted operation, specific search rankings, sales, revenue, or results from third-party services.",
  provider_name:
    "Kenneth L. Powell",
  provider_business:
    "Avwakin Development",
  client_signer_name: "",
  client_signer_title: "",
};


const SCOPE_OF_WORK_DEFAULTS = {
  effective_date: new Date()
    .toISOString()
    .slice(0, 10),
  project_objective:
    "The objective of this project is to provide the client with the agreed website, application, automation, or related development services described in this Scope of Work.",
  project_overview:
    "Avwakin Development will complete the work described below according to the linked proposal, approved project records, and any later written changes accepted by both parties.",
  deliverables:
    "Final deliverables will include the approved project work, configured features, and any files or access information specifically listed in the linked proposal or project records.",
  included_services:
    "Included services are limited to the work specifically described in this Scope of Work, the linked proposal, and approved project items.",
  excluded_services:
    "Services not expressly listed are excluded. Additional pages, features, integrations, content entry, maintenance, hosting, licensing, or third-party costs may require a separate estimate or written change request.",
  milestones:
    "Project milestones will be based on planning, design or setup, development, client review, revisions, testing, and final delivery as applicable to the project.",
  estimated_timeline:
    "The estimated timeline begins after the required deposit, credentials, content, approvals, and other client-supplied materials are received.",
  client_responsibilities:
    "The client will provide timely content, branding materials, credentials, approvals, feedback, and access needed to complete the project.",
  provider_responsibilities:
    "Avwakin Development will perform the approved work with reasonable care, communicate material issues, and provide the deliverables described in this Scope of Work.",
  required_client_materials:
    "Required client materials may include written content, images, logos, brand guidance, account credentials, product information, legal text, and timely approvals.",
  dependencies_assumptions:
    "The project assumes timely client cooperation and continued availability of required third-party services. Delays, service changes, or missing materials may affect the schedule.",
  acceptance_criteria:
    "Deliverables will be considered accepted when they substantially match the approved scope and the client confirms acceptance or does not report a material scope-related issue within the agreed review period.",
  revision_allowance:
    "Two reasonable revision rounds are included unless the linked proposal states otherwise. Additional revisions may require a written change request and additional fees.",
  change_request_process:
    "Changes outside the approved scope must be requested in writing. Avwakin Development will provide any resulting price or timeline adjustment before beginning the additional work.",
  completion_requirements:
    "The project will be considered complete after the approved deliverables are provided, required testing is completed, and any remaining balance due under the project is paid.",
  additional_terms: "",
  provider_name:
    "Kenneth L. Powell",
  provider_business:
    "Avwakin Development",
  client_approver_name: "",
  client_approver_title: "",
};


const CONTRACT_DEFAULTS = {
  effective_date: new Date()
    .toISOString()
    .slice(0, 10),
  start_date: "",
  end_date: "",
  services:
    "Avwakin Development will provide the website development, application, automation, consulting, or related technical services described in the linked proposal, Scope of Work, approved project records, and written changes accepted by both parties.",
  compensation:
    "The client agrees to pay the fees, deposits, installments, expenses, and other approved charges identified in the linked proposal, invoices, and approved written changes.",
  payment_schedule:
    "Payments are due according to the linked proposal and invoices. Avwakin Development may pause work when an invoice is overdue and may adjust the schedule to account for the delay.",
  provider_responsibilities:
    "Avwakin Development will perform the approved services with reasonable care, communicate material issues, and provide the deliverables specifically included in the approved scope.",
  client_responsibilities:
    "The client will provide timely content, credentials, access, approvals, feedback, legal text, and other materials reasonably required to perform the services.",
  confidentiality:
    "Each party will use reasonable care to protect confidential information received from the other party and will use that information only as needed to perform or receive the contracted services.",
  intellectual_property:
    "Upon full payment, the client will own the final custom deliverables created specifically for the project. Avwakin Development retains ownership of preexisting materials, reusable code, tools, methods, processes, templates, and general technical knowledge.",
  third_party_services:
    "Third-party products and services may be governed by separate terms, availability, licensing, and fees. Avwakin Development is not responsible for changes, outages, policy decisions, or discontinuation by third-party providers.",
  warranties:
    "Avwakin Development will correct material defects in its custom work reported within the agreed review or support period. No warranty applies to client changes, third-party systems, misuse, unsupported modifications, or matters outside the approved scope.",
  limitation_of_liability:
    "To the extent permitted by law, neither party will be liable for indirect, incidental, special, or consequential damages. Avwakin Development does not guarantee search rankings, revenue, sales, traffic, uninterrupted availability, or results controlled by third parties.",
  termination:
    "Either party may terminate this contract by written notice. The client remains responsible for completed work, approved expenses, nonrefundable costs, and work performed through the termination date.",
  dispute_resolution:
    "The parties will first attempt to resolve disputes through good-faith discussion. Any unresolved dispute will be handled under the governing law and venue stated in this contract.",
  governing_law:
    "This contract will be governed by the laws of the State of Oregon, without regard to conflict-of-law rules.",
  entire_agreement:
    "This contract, together with any linked proposal, Scope of Work, approved change request, and invoice, represents the complete agreement between the parties concerning the services and replaces prior discussions on the same subject.",
  additional_terms: "",
  provider_name:
    "Kenneth L. Powell",
  provider_business:
    "Avwakin Development",
  client_signer_name: "",
  client_signer_title: "",
};


const PROPOSAL_DOCUMENT_DEFAULTS = {
  executive_summary:
    "This proposal outlines the recommended services, deliverables, timeline, responsibilities, and investment for the proposed project.",
  project_objectives:
    "The project objectives are to provide the client with a professional, reliable, and maintainable solution aligned with the approved business and technical requirements.",
  deliverables:
    "Deliverables include the services and project items listed in the linked proposal, together with any approved written changes.",
  assumptions:
    "This proposal assumes timely client feedback, access, content, credentials, and approvals. Delays or scope changes may affect pricing and delivery dates.",
  exclusions:
    "Items not expressly included in the linked proposal or approved project records are excluded and may require a separate estimate or change request.",
  client_responsibilities:
    "The client will provide timely content, access, credentials, feedback, approvals, and any legal or policy text required for the project.",
  provider_responsibilities:
    "Avwakin Development will perform the approved services with reasonable care, communicate material issues, and deliver the approved project work.",
  acceptance_terms:
    "The proposal may be accepted through written approval, signature, deposit payment, or another method agreed by both parties.",
  additional_terms: "",
  provider_name: "Kenneth L. Powell",
  provider_business: "Avwakin Development",
  client_approver_name: "",
  client_approver_title: "",
};

const RECEIPT_DEFAULTS = {
  payment_date: new Date().toISOString().slice(0, 10),
  payment_method: "",
  amount_received: "",
  transaction_reference: "",
  payment_description:
    "Payment received for approved Avwakin Development services.",
  remaining_balance: "",
  payer_name: "",
  received_by: "Kenneth L. Powell",
  additional_notes: "",
};

const COMPLETION_DEFAULTS = {
  completion_date: new Date().toISOString().slice(0, 10),
  completion_summary:
    "Avwakin Development has completed the approved project work described in the linked proposal, Scope of Work, contract, and approved change requests.",
  delivered_items:
    "The completed project deliverables, files, configured features, and approved access information have been provided or made available to the client.",
  outstanding_items:
    "No outstanding project items remain unless specifically listed in this section.",
  credentials_transferred:
    "Applicable project credentials and account access have been transferred or confirmed with the client where required.",
  training_provided:
    "Reasonable project handoff guidance or training has been provided when included in the approved scope.",
  support_period:
    "Any included post-launch support is limited to the period and services stated in the linked proposal or agreement.",
  warranty_period:
    "Material defects in Avwakin Development's custom work must be reported during the agreed review or warranty period. Third-party services and client changes are excluded.",
  final_balance_terms:
    "Any remaining project balance is due according to the final invoice and payment terms.",
  client_acceptance:
    "By signing below, the client confirms receipt of the listed deliverables and acknowledges project completion, subject to any specifically documented outstanding items.",
  additional_notes: "",
  provider_name: "Kenneth L. Powell",
  provider_business: "Avwakin Development",
  client_approver_name: "",
  client_approver_title: "",
};

const OTHER_DOCUMENT_DEFAULTS = {
  effective_date: new Date().toISOString().slice(0, 10),
  purpose: "",
  summary: "",
  document_body: "",
  action_items: "",
  references: "",
  additional_notes: "",
  prepared_by: "Kenneth L. Powell",
  prepared_for: "",
};

const EMPTY_DOCUMENT_FORM = {
  client_id: "",
  project_id: "",
  proposal_id: "",
  invoice_id: "",
  template_id: "",
  document_type: "proposal",
  title: "",
  status: "draft",
  generated_content: "",
  document_details: {},
  pdf_file_url: "",
  sent: false,
  finalized: false,
  internal_notes: "",
};

const EMPTY_TEMPLATE_FORM = {
  template_name: "",
  document_type: "proposal",
  title_format: "",
  header_content: "",
  body_content: "",
  footer_content: "",
  default_terms: "",
  active: true,
  is_default: false,
};

const DOCUMENT_TYPES = [
  "proposal",
  "invoice",
  "agreement",
  "contract",
  "scope_of_work",
  "receipt",
  "completion",
  "other",
];

const DOCUMENT_STATUSES = [
  "draft",
  "generated",
  "sent",
  "finalized",
  "cancelled",
  "archived",
];

function optionalText(value) {
  const cleaned = cleanText(value);

  return cleaned || null;
}

function formatLabel(value) {
  return safeString(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function cleanText(value) {
  return safeString(value).trim();
}

function normalizeDocumentDetails(value) {
  if (!value) {
    return {};
  }

  if (
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    return value;
  }

  try {
    const parsed = JSON.parse(
      safeString(value)
    );

    return (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
        ? parsed
        : {}
    );
  } catch (error) {
    return {};
  }
}

function getAgreementDetails(value) {
  return {
    ...AGREEMENT_DEFAULTS,
    ...normalizeDocumentDetails(value),
  };
}

function getScopeOfWorkDetails(value) {
  return {
    ...SCOPE_OF_WORK_DEFAULTS,
    ...normalizeDocumentDetails(value),
  };
}


function getContractDetails(value) {
  return {
    ...CONTRACT_DEFAULTS,
    ...normalizeDocumentDetails(value),
  };
}


function getProposalDocumentDetails(value) {
  return {
    ...PROPOSAL_DOCUMENT_DEFAULTS,
    ...normalizeDocumentDetails(value),
  };
}

function getReceiptDetails(value) {
  return {
    ...RECEIPT_DEFAULTS,
    ...normalizeDocumentDetails(value),
  };
}

function getCompletionDetails(value) {
  return {
    ...COMPLETION_DEFAULTS,
    ...normalizeDocumentDetails(value),
  };
}

function getOtherDocumentDetails(value) {
  return {
    ...OTHER_DOCUMENT_DEFAULTS,
    ...normalizeDocumentDetails(value),
  };
}

function hasStoredPdf(document) {
  return safeString(
    document?.pdf_file_url
  ).includes(
    "/api/development/document-pdf/"
  );
}

function getPdfFileName(document) {
  const reference =
    document?.document_number ||
    document?.title ||
    `development-document-${document?.id || "file"}`;

  const sanitized = safeString(reference)
    .trim()
    .replaceAll("\\", "/")
    .split("/")
    .pop()
    .replace(
      /[^a-zA-Z0-9._-]+/g,
      "-"
    )
    .replace(
      /-+/g,
      "-"
    )
    .replace(
      /^[-.]+|[-.]+$/g,
      ""
    );

  const fileName =
    sanitized ||
    "development-document";

  return fileName
    .toLowerCase()
    .endsWith(".pdf")
    ? fileName
    : `${fileName}.pdf`;
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
  ).format(date);
}

function getClientName(client) {
  if (!client) {
    return "Unknown client";
  }

  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${client.id}`
  );
}

function normalizeDocument(document = {}) {
  return {
    client_id:
      document.client_id == null
        ? ""
        : String(document.client_id),

    project_id:
      document.project_id == null
        ? ""
        : String(document.project_id),

    proposal_id:
      document.proposal_id == null
        ? ""
        : String(document.proposal_id),

    invoice_id:
      document.invoice_id == null
        ? ""
        : String(document.invoice_id),

    template_id:
      document.template_id == null
        ? ""
        : String(document.template_id),

    document_type:
      safeString(
        document.document_type
      ) || "proposal",

    title:
      safeString(document.title),

    status:
      safeString(document.status) ||
      "draft",

    generated_content:
      safeString(
        document.generated_content
      ),

    document_details:
      normalizeDocumentDetails(
        document.document_details
      ),

    pdf_file_url:
      safeString(
        document.pdf_file_url
      ),

    sent:
      Number(document.sent) === 1,

    finalized:
      Number(document.finalized) === 1,

    internal_notes:
      safeString(
        document.internal_notes
      ),
  };
}

function normalizeTemplate(template = {}) {
  return {
    template_name:
      safeString(
        template.template_name
      ),

    document_type:
      safeString(
        template.document_type
      ) || "proposal",

    title_format:
      safeString(
        template.title_format
      ),

    header_content:
      safeString(
        template.header_content
      ),

    body_content:
      safeString(
        template.body_content
      ),

    footer_content:
      safeString(
        template.footer_content
      ),

    default_terms:
      safeString(
        template.default_terms
      ),

    active:
      Number(template.active) === 1,

    is_default:
      Number(
        template.is_default
      ) === 1,
  };
}

function DevelopmentDocuments() {
  const [activeTab, setActiveTab] =
    useState("documents");

  const [documents, setDocuments] =
    useState([]);

  const [templates, setTemplates] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [projects, setProjects] =
    useState([]);

  const [proposals, setProposals] =
    useState([]);

  const [invoices, setInvoices] =
    useState([]);

  const [selectedDocument, setSelectedDocument] =
    useState(null);

  const [selectedTemplate, setSelectedTemplate] =
    useState(null);

  const [documentForm, setDocumentForm] =
    useState(EMPTY_DOCUMENT_FORM);

  const [templateForm, setTemplateForm] =
    useState(EMPTY_TEMPLATE_FORM);

  const [showDocumentForm, setShowDocumentForm] =
    useState(false);

  const [showTemplateForm, setShowTemplateForm] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [typeFilter, setTypeFilter] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("all");

  const [clientFilter, setClientFilter] =
    useState("");

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const [
    pdfPreviewData,
    setPdfPreviewData,
  ] = useState(null);

  const [
    pdfActionDocumentId,
    setPdfActionDocumentId,
  ] = useState(null);

  const [
    emailDocumentData,
    setEmailDocumentData,
  ] = useState(null);

  const [
    isEmailingDocument,
    setIsEmailingDocument,
  ] = useState(false);

  const [
    emailFeedback,
    setEmailFeedback,
  ] = useState({
    type: "",
    message: "",
  });

  const [
    emailHistoryData,
    setEmailHistoryData,
  ] = useState(null);

  const [
    isLoadingEmailHistory,
    setIsLoadingEmailHistory,
  ] = useState(false);

  const loadData = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const [
          documentResponse,
          templateResponse,
          clientResponse,
          projectResponse,
          proposalResponse,
          invoiceResponse,
        ] = await Promise.all([
          Api.getDevelopmentDocuments(),
          Api.getDevelopmentDocumentTemplates(),
          Api.getDevelopmentClients(),
          Api.getDevelopmentProjects(),
          Api.getDevelopmentProposals(),
          Api.getDevelopmentInvoices(),
        ]);

        setDocuments(
          documentResponse?.documents || []
        );

        setTemplates(
          templateResponse?.templates || []
        );

        setClients(
          clientResponse?.clients || []
        );

        setProjects(
          projectResponse?.projects || []
        );

        setProposals(
          proposalResponse?.proposals || []
        );

        setInvoices(
          invoiceResponse?.invoices || []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Documents could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientMap = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          Number(client.id),
          client,
        ])
      ),
    [clients]
  );

  const projectMap = useMemo(
    () =>
      new Map(
        projects.map((project) => [
          Number(project.id),
          project,
        ])
      ),
    [projects]
  );

  const proposalMap = useMemo(
    () =>
      new Map(
        proposals.map((proposal) => [
          Number(proposal.id),
          proposal,
        ])
      ),
    [proposals]
  );

  const invoiceMap = useMemo(
    () =>
      new Map(
        invoices.map((invoice) => [
          Number(invoice.id),
          invoice,
        ])
      ),
    [invoices]
  );

  const templateMap = useMemo(
    () =>
      new Map(
        templates.map((template) => [
          Number(template.id),
          template,
        ])
      ),
    [templates]
  );

  const availableProjects = useMemo(() => {
    if (!documentForm.client_id) {
      return projects;
    }

    return projects.filter(
      (project) =>
        String(project.client_id) ===
        String(documentForm.client_id)
    );
  }, [
    projects,
    documentForm.client_id,
  ]);

  const availableProposals = useMemo(() => {
    return proposals.filter((proposal) => {
      if (
        documentForm.client_id &&
        String(proposal.client_id) !==
          String(documentForm.client_id)
      ) {
        return false;
      }

      if (
        documentForm.project_id &&
        String(proposal.project_id) !==
          String(documentForm.project_id)
      ) {
        return false;
      }

      return true;
    });
  }, [
    proposals,
    documentForm.client_id,
    documentForm.project_id,
  ]);

  const availableInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (
        documentForm.client_id &&
        String(invoice.client_id) !==
          String(documentForm.client_id)
      ) {
        return false;
      }

      if (
        documentForm.project_id &&
        String(invoice.project_id) !==
          String(documentForm.project_id)
      ) {
        return false;
      }

      return true;
    });
  }, [
    invoices,
    documentForm.client_id,
    documentForm.project_id,
  ]);

  const availableTemplates = useMemo(
    () =>
      templates.filter(
        (template) =>
          Number(template.active) === 1 &&
          (
            !documentForm.document_type ||
            template.document_type ===
              documentForm.document_type
          )
      ),
    [
      templates,
      documentForm.document_type,
    ]
  );

  const filteredDocuments = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    return documents.filter((document) => {
      if (
        typeFilter &&
        document.document_type !== typeFilter
      ) {
        return false;
      }

      if (
        statusFilter === "all" &&
        document.status === "archived"
      ) {
        return false;
      }

      if (
        statusFilter !== "all" &&
        document.status !==
          statusFilter
      ) {
        return false;
      }

      if (
        clientFilter &&
        String(document.client_id) !==
          String(clientFilter)
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const client =
        clientMap.get(
          Number(document.client_id)
        );

      const project =
        projectMap.get(
          Number(document.project_id)
        );

      const searchableText = [
        document.title,
        document.document_number,
        document.document_type,
        document.status,
        document.internal_notes,
        getClientName(client),
        project?.title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(
        normalizedSearch
      );
    });
  }, [
    documents,
    search,
    typeFilter,
    statusFilter,
    clientFilter,
    clientMap,
    projectMap,
  ]);

  const filteredTemplates = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    return templates.filter((template) => {
      if (
        typeFilter &&
        template.document_type !== typeFilter
      ) {
        return false;
      }

      if (
        statusFilter === "active" &&
        Number(template.active) !== 1
      ) {
        return false;
      }

      if (
        statusFilter === "inactive" &&
        Number(template.active) === 1
      ) {
        return false;
      }

      if (
        statusFilter === "default" &&
        Number(template.is_default) !== 1
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchableText = [
        template.template_name,
        template.document_type,
        template.title_format,
        template.header_content,
        template.body_content,
        template.default_terms,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(
        normalizedSearch
      );
    });
  }, [
    templates,
    search,
    typeFilter,
    statusFilter,
  ]);

  const documentSummary = useMemo(
    () => {
      const visibleDocuments =
        documents.filter(
          (document) =>
            document.status !==
            "archived"
        );

      return {
        total:
          visibleDocuments.length,

        draft:
          visibleDocuments.filter(
            (document) =>
              document.status ===
              "draft"
          ).length,

        sent:
          visibleDocuments.filter(
            (document) =>
              Number(
                document.sent
              ) === 1
          ).length,

        finalized:
          visibleDocuments.filter(
            (document) =>
              Number(
                document.finalized
              ) === 1
          ).length,

        archived:
          documents.filter(
            (document) =>
              document.status ===
              "archived"
          ).length,
      };
    },
    [documents]
  );

  const templateSummary = useMemo(
    () => ({
      total: templates.length,

      active: templates.filter(
        (template) =>
          Number(template.active) === 1
      ).length,

      inactive: templates.filter(
        (template) =>
          Number(template.active) !== 1
      ).length,

      defaults: templates.filter(
        (template) =>
          Number(template.is_default) === 1
      ).length,
    }),
    [templates]
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const changeTab = (tab) => {
    clearMessages();
    setActiveTab(tab);
    setSearch("");
    setTypeFilter("");
    setClientFilter("");
    setStatusFilter("all");
    setShowDocumentForm(false);
    setShowTemplateForm(false);
  };

  const openNewDocumentForm = () => {
    clearMessages();
    setSelectedDocument(null);
    setDocumentForm(
      EMPTY_DOCUMENT_FORM
    );
    setShowDocumentForm(true);
  };

  const openEditDocumentForm = (
    document
  ) => {
    clearMessages();
    setSelectedDocument(document);
    setDocumentForm(
      normalizeDocument(document)
    );
    setShowDocumentForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeDocumentForm = () => {
    setSelectedDocument(null);
    setDocumentForm(
      EMPTY_DOCUMENT_FORM
    );
    setShowDocumentForm(false);
  };

  const handleDocumentChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setDocumentForm((current) => {
      const next = {
        ...current,
        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      if (name === "client_id") {
        next.project_id = "";
        next.proposal_id = "";
        next.invoice_id = "";
      }

      if (name === "project_id") {
        next.proposal_id = "";
        next.invoice_id = "";
      }

      if (name === "document_type") {
        next.template_id = "";

        if (value === "agreement") {
          next.document_details =
            getAgreementDetails(
              current.document_details
            );
        } else if (
          value === "scope_of_work"
        ) {
          next.document_details =
            getScopeOfWorkDetails(
              current.document_details
            );
        } else if (
          value === "contract"
        ) {
          next.document_details =
            getContractDetails(
              current.document_details
            );
        }
        else if (value === "proposal") {
          next.document_details =
            getProposalDocumentDetails(current.document_details);
        } else if (value === "receipt") {
          next.document_details =
            getReceiptDetails(current.document_details);
        } else if (value === "completion") {
          next.document_details =
            getCompletionDetails(current.document_details);
        } else if (value === "other") {
          next.document_details =
            getOtherDocumentDetails(current.document_details);
        }
      }

      if (
        name.startsWith(
          "document_details."
        )
      ) {
        const detailName =
          name.replace(
            "document_details.",
            ""
          );

        next.document_details = {
          ...normalizeDocumentDetails(
            current.document_details
          ),
          [detailName]: value,
        };

        delete next[name];
      }

      return next;
    });
  };

  const validateDocumentForm = () => {
    if (!documentForm.client_id) {
      return "A client is required.";
    }

    if (
      !cleanText(
        documentForm.title
      )
    ) {
      return "Document title is required.";
    }

    if (
      !documentForm.document_type
    ) {
      return "Document type is required.";
    }

    if (
      documentForm.document_type ===
      "agreement"
    ) {
      const details =
        getAgreementDetails(
          documentForm.document_details
        );

      if (!details.effective_date) {
        return "Agreement effective date is required.";
      }

      if (
        !cleanText(
          details.project_scope
        )
      ) {
        return "Agreement project scope is required.";
      }

      if (
        !cleanText(
          details.payment_terms
        )
      ) {
        return "Agreement payment terms are required.";
      }

      if (
        !cleanText(
          details.client_responsibilities
        )
      ) {
        return "Client responsibilities are required.";
      }
    }


    if (
      documentForm.document_type ===
      "scope_of_work"
    ) {
      const details =
        getScopeOfWorkDetails(
          documentForm.document_details
        );

      if (!details.effective_date) {
        return "Scope of Work effective date is required.";
      }

      if (
        !cleanText(
          details.project_objective
        )
      ) {
        return "Project objective is required.";
      }

      if (
        !cleanText(
          details.deliverables
        )
      ) {
        return "Deliverables are required.";
      }

      if (
        !cleanText(
          details.client_responsibilities
        )
      ) {
        return "Client responsibilities are required.";
      }
    }


    if (
      documentForm.document_type ===
      "contract"
    ) {
      const details =
        getContractDetails(
          documentForm.document_details
        );

      if (!details.effective_date) {
        return "Contract effective date is required.";
      }

      if (
        !cleanText(
          details.services
        )
      ) {
        return "Contract services are required.";
      }

      if (
        !cleanText(
          details.compensation
        )
      ) {
        return "Contract compensation terms are required.";
      }

      if (
        !cleanText(
          details.client_responsibilities
        )
      ) {
        return "Client responsibilities are required.";
      }
    }



    if (documentForm.document_type === "proposal") {
      const details = getProposalDocumentDetails(documentForm.document_details);
      if (!cleanText(details.executive_summary)) {
        return "Proposal executive summary is required.";
      }
      if (!cleanText(details.deliverables)) {
        return "Proposal deliverables are required.";
      }
    }

    if (documentForm.document_type === "receipt") {
      const details = getReceiptDetails(documentForm.document_details);
      if (!details.payment_date) {
        return "Receipt payment date is required.";
      }
      if (!(Number(details.amount_received) > 0)) {
        return "Receipt amount received must be greater than zero.";
      }
    }

    if (documentForm.document_type === "completion") {
      const details = getCompletionDetails(documentForm.document_details);
      if (!details.completion_date) {
        return "Completion date is required.";
      }
      if (!cleanText(details.delivered_items)) {
        return "Delivered items are required.";
      }
    }

    if (documentForm.document_type === "other") {
      const details = getOtherDocumentDetails(documentForm.document_details);
      if (!cleanText(details.document_body)) {
        return "Document body is required.";
      }
    }

    return "";
  };

  const buildDocumentPayload = () => ({
    client_id:
      Number(documentForm.client_id),

    project_id:
      documentForm.project_id
        ? Number(
            documentForm.project_id
          )
        : null,

    proposal_id:
      documentForm.proposal_id
        ? Number(
            documentForm.proposal_id
          )
        : null,

    invoice_id:
      documentForm.invoice_id
        ? Number(
            documentForm.invoice_id
          )
        : null,

    template_id:
      documentForm.template_id
        ? Number(
            documentForm.template_id
          )
        : null,

    document_type:
      cleanText(
        documentForm.document_type
      ),

    title:
      cleanText(
        documentForm.title
      ),

    status:
      cleanText(
        documentForm.status
      ) || "draft",

    generated_content:
      optionalText(
        documentForm.generated_content
      ),

    document_details:
      documentForm.document_type ===
      "agreement"
        ? getAgreementDetails(
            documentForm.document_details
          )
        : documentForm.document_type ===
            "scope_of_work"
          ? getScopeOfWorkDetails(
              documentForm.document_details
            )
          : documentForm.document_type === "contract"
            ? getContractDetails(documentForm.document_details)
            : documentForm.document_type === "proposal"
              ? getProposalDocumentDetails(documentForm.document_details)
              : documentForm.document_type === "receipt"
                ? getReceiptDetails(documentForm.document_details)
                : documentForm.document_type === "completion"
                  ? getCompletionDetails(documentForm.document_details)
                  : documentForm.document_type === "other"
                    ? getOtherDocumentDetails(documentForm.document_details)
                    : normalizeDocumentDetails(documentForm.document_details),

    pdf_file_url:
      optionalText(
        documentForm.pdf_file_url
      ),

    sent:
      Boolean(documentForm.sent),

    finalized:
      Boolean(
        documentForm.finalized
      ),

    internal_notes:
      optionalText(
        documentForm.internal_notes
      ),
  });

  const handleDocumentSubmit =
    async (event) => {
      event.preventDefault();
      clearMessages();

      const validationError =
        validateDocumentForm();

      if (validationError) {
        setError(validationError);
        return;
      }

      setIsSaving(true);

      try {
        const payload =
          buildDocumentPayload();

        if (selectedDocument) {
          await Api.updateDevelopmentDocument(
            selectedDocument.id,
            payload
          );

          setSuccess(
            "Document updated successfully."
          );
        } else {
          await Api.createDevelopmentDocument(
            payload
          );

          setSuccess(
            "Document created successfully."
          );
        }

        await loadData();
        closeDocumentForm();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The document could not be saved."
        );
      } finally {
        setIsSaving(false);
      }
    };

  const updateDocument = async (
    document,
    changes,
    message
  ) => {
    clearMessages();

    try {
      await Api.updateDevelopmentDocument(
        document.id,
        changes
      );

      setSuccess(message);
      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The document could not be updated."
      );
    }
  };

  const markDocumentSent = (
    document
  ) => {
    updateDocument(
      document,
      {
        sent: true,
      },
      "Document marked as sent."
    );
  };

  const finalizeDocument = (
    document
  ) => {
    updateDocument(
      document,
      {
        finalized: true,
      },
      "Document finalized."
    );
  };

  const restoreDocument = (
    document
  ) => {
    const restoredStatus =
      Number(
        document.finalized
      ) === 1
        ? "finalized"
        : Number(
              document.sent
            ) === 1
          ? "sent"
          : hasStoredPdf(
                document
              )
            ? "generated"
            : "draft";

    updateDocument(
      document,
      {
        status:
          restoredStatus,
      },
      "Document restored successfully."
    );
  };

  const deleteDocument = async (
    document
  ) => {
    const confirmed =
      window.confirm(
        `Delete document "${document.title}"?`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    try {
      await Api.deleteDevelopmentDocument(
        document.id
      );

      setSuccess(
        "Document deleted successfully."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The document could not be deleted."
      );
    }
  };

const openPdfPreview =
  async (document) => {
    clearMessages();

    setPdfActionDocumentId(
      document.id
    );

    try {
      let proposal = null;
      let invoice = null;
      let items = [];

      if (document.proposal_id) {
        const proposalResponse =
          await Api.getDevelopmentProposal(
            document.proposal_id
          );

        proposal =
          proposalResponse?.proposal ||
          proposalMap.get(
            Number(
              document.proposal_id
            )
          ) ||
          null;

        items =
          proposalResponse?.items ||
          [];
      } else if (
        document.invoice_id
      ) {
        const invoiceResponse =
          await Api.getDevelopmentInvoice(
            document.invoice_id
          );

        invoice =
          invoiceResponse?.invoice ||
          invoiceMap.get(
            Number(
              document.invoice_id
            )
          ) ||
          null;

        items =
          invoiceResponse?.items ||
          [];
      }

      const client =
        clientMap.get(
          Number(
            document.client_id
          )
        ) ||
        {
          id: document.client_id,
          first_name:
            document.first_name,
          last_name:
            document.last_name,
          business_name:
            document.business_name,
        };

      const project =
        projectMap.get(
          Number(
            document.project_id
          )
        ) ||
        (
          document.project_id
            ? {
                id:
                  document.project_id,

                title:
                  document.project_title,
              }
            : null
        );

      const template =
        templateMap.get(
          Number(
            document.template_id
          )
        ) ||
        null;

      setPdfPreviewData({
        document,
        client,
        project,
        proposal,
        invoice,
        template,
        items,
      });
    } catch (requestError) {
      setError(
        requestError.message ||
          "The information needed for the PDF could not be loaded."
      );
    } finally {
      setPdfActionDocumentId(
        null
      );
    }
  };

const closePdfPreview = () => {
  setPdfPreviewData(null);
};

const handlePdfStored =
  async (response) => {
    setPdfPreviewData(null);

    setSuccess(
      response?.message ||
        "PDF generated and stored successfully."
    );

    await loadData();
  };

const openStoredPdf =
  async (
    document,
    download = false
  ) => {
    clearMessages();

    setPdfActionDocumentId(
      document.id
    );

    try {
      const blob =
        await Api.getDevelopmentDocumentPdfBlob(
          document.id,
          download
        );

      const objectUrl =
        URL.createObjectURL(blob);

      if (download) {
        const link =
          window.document.createElement(
            "a"
          );

        link.href = objectUrl;

        link.download =
          getPdfFileName(
            document
          );

        window.document.body.appendChild(
          link
        );

        link.click();

        window.document.body.removeChild(
          link
        );

        URL.revokeObjectURL(
          objectUrl
        );
      } else {
        const openedWindow =
          window.open(
            objectUrl,
            "_blank",
            "noopener,noreferrer"
          );

        if (!openedWindow) {
          throw new Error(
            "The browser blocked the PDF window. Please allow popups for this site."
          );
        }

        /*
         * Give the new browser tab time to
         * finish loading before releasing
         * the temporary Blob URL.
         */
        window.setTimeout(() => {
          URL.revokeObjectURL(
            objectUrl
          );
        }, 60000);
      }
    } catch (requestError) {
      setError(
        requestError.message ||
          "The stored PDF could not be opened."
      );
    } finally {
      setPdfActionDocumentId(
        null
      );
    }
  };

  const deleteStoredPdf = async (document) => {
    const confirmed =
      window.confirm(
        `Delete the stored PDF for "${document.title}"? The document record itself will remain.`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    setPdfActionDocumentId(
      document.id
    );

    try {
      const response =
        await Api.deleteDevelopmentDocumentPdf(
          document.id
        );

      setSuccess(
        response?.message ||
          "Stored PDF deleted successfully."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The stored PDF could not be deleted."
      );
    } finally {
      setPdfActionDocumentId(
        null
      );
    }
  };

  const openDocumentEmail = (
    document
  ) => {
    clearMessages();

    if (!hasStoredPdf(document)) {
      setError(
        "Generate and save the PDF before emailing this document."
      );

      return;
    }

    const client =
      clientMap.get(
        Number(
          document.client_id
        )
      );

    const clientEmail =
      safeString(
        client?.email ||
          document.client_email
      );

    const clientName =
      getClientName(
        client || {
          id:
            document.client_id,
          first_name:
            document.first_name,
          last_name:
            document.last_name,
          business_name:
            document.business_name,
        }
      );

    const reference =
      document.document_number ||
      document.title;

    setEmailFeedback({
      type: "",
      message: "",
    });

    setEmailDocumentData({
      document,
      to: clientEmail,
      subject:
        `${document.title} — Avwakin Development`,
      message:
        `Hello ${clientName},\n\nAttached is ${document.title} (${reference}) for your records and review.\n\nPlease let me know if you have any questions.\n\nThank you,\nKenneth L. Powell\nAvwakin Development`,
    });
  };

  const sendDocumentEmail =
    async (event) => {
      event.preventDefault();

      if (
        !emailDocumentData
      ) {
        return;
      }

      const recipient =
        cleanText(
          emailDocumentData.to
        );

      const subject =
        cleanText(
          emailDocumentData.subject
        );

      const message =
        cleanText(
          emailDocumentData.message
        );

      if (!recipient) {
        setError(
          "A recipient email address is required."
        );

        return;
      }

      if (!subject) {
        setError(
          "An email subject is required."
        );

        return;
      }

      if (!message) {
        setError(
          "An email message is required."
        );

        return;
      }

      clearMessages();

      setEmailFeedback({
        type: "status",
        message:
          "Sending the email and attaching the stored PDF...",
      });

      setIsEmailingDocument(
        true
      );

      try {
        const response =
          await Api.emailDevelopmentDocument(
            emailDocumentData
              .document.id,
            {
              to: recipient,
              subject,
              message,
            }
          );

        const successMessage =
          response?.message ||
          `Document emailed successfully to ${recipient}.`;

        setEmailFeedback({
          type: "success",
          message:
            successMessage,
        });

        setSuccess(
          successMessage
        );

        await loadData();

        window.setTimeout(() => {
          setEmailDocumentData(
            null
          );

          setEmailFeedback({
            type: "",
            message: "",
          });
        }, 1400);
      } catch (
        requestError
      ) {
        const errorMessage =
          requestError.message ||
          "The document could not be emailed.";

        setEmailFeedback({
          type: "error",
          message:
            errorMessage,
        });

        setError(
          errorMessage
        );
      } finally {
        setIsEmailingDocument(
          false
        );
      }
    };

  const openDocumentEmailHistory =
    async (document) => {
      clearMessages();

      setIsLoadingEmailHistory(
        true
      );

      setEmailHistoryData({
        document,
        history: [],
      });

      try {
        const response =
          await Api.getDevelopmentDocumentEmailHistory(
            document.id
          );

        setEmailHistoryData({
          document,
          history:
            response?.history ||
            [],
        });
      } catch (requestError) {
        setEmailHistoryData(
          null
        );

        setError(
          requestError.message ||
            "Email history could not be loaded."
        );
      } finally {
        setIsLoadingEmailHistory(
          false
        );
      }
    };

  const openNewTemplateForm = () => {
    clearMessages();
    setSelectedTemplate(null);
    setTemplateForm(
      EMPTY_TEMPLATE_FORM
    );
    setShowTemplateForm(true);
  };

  const openEditTemplateForm = (
    template
  ) => {
    clearMessages();
    setSelectedTemplate(template);
    setTemplateForm(
      normalizeTemplate(template)
    );
    setShowTemplateForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeTemplateForm = () => {
    setSelectedTemplate(null);
    setTemplateForm(
      EMPTY_TEMPLATE_FORM
    );
    setShowTemplateForm(false);
  };

  const handleTemplateChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setTemplateForm((current) => ({
      ...current,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const validateTemplateForm = () => {
    if (
      !cleanText(
        templateForm.template_name
      )
    ) {
      return "Template name is required.";
    }

    if (
      !templateForm.document_type
    ) {
      return "Document type is required.";
    }

    if (
      !cleanText(
        templateForm.body_content
      )
    ) {
      return "Template body content is required.";
    }

    return "";
  };

  const buildTemplatePayload = () => ({
    template_name:
      cleanText(
        templateForm.template_name
      ),

    document_type:
      cleanText(
        templateForm.document_type
      ),

    title_format:
      optionalText(
        templateForm.title_format
      ),

    header_content:
      optionalText(
        templateForm.header_content
      ),

    body_content:
      cleanText(
        templateForm.body_content
      ),

    footer_content:
      optionalText(
        templateForm.footer_content
      ),

    default_terms:
      optionalText(
        templateForm.default_terms
      ),

    active:
      Boolean(templateForm.active),

    is_default:
      Boolean(
        templateForm.is_default
      ),
  });

  const handleTemplateSubmit =
    async (event) => {
      event.preventDefault();
      clearMessages();

      const validationError =
        validateTemplateForm();

      if (validationError) {
        setError(validationError);
        return;
      }

      setIsSaving(true);

      try {
        const payload =
          buildTemplatePayload();

        if (selectedTemplate) {
          await Api.updateDevelopmentDocumentTemplate(
            selectedTemplate.id,
            payload
          );

          setSuccess(
            "Template updated successfully."
          );
        } else {
          await Api.createDevelopmentDocumentTemplate(
            payload
          );

          setSuccess(
            "Template created successfully."
          );
        }

        await loadData();
        closeTemplateForm();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The template could not be saved."
        );
      } finally {
        setIsSaving(false);
      }
    };

  const toggleTemplateActive = async (
    template
  ) => {
    clearMessages();

    const nextActive =
      Number(template.active) !== 1;

    try {
      await Api.updateDevelopmentDocumentTemplate(
        template.id,
        {
          active: nextActive,
        }
      );

      setSuccess(
        nextActive
          ? "Template activated."
          : "Template deactivated."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The template could not be updated."
      );
    }
  };

  const toggleTemplateDefault = async (
    template
  ) => {
    clearMessages();

    const nextDefault =
      Number(template.is_default) !== 1;

    try {
      await Api.updateDevelopmentDocumentTemplate(
        template.id,
        {
          is_default: nextDefault,
        }
      );

      setSuccess(
        nextDefault
          ? "Template set as default."
          : "Template removed as default."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The template could not be updated."
      );
    }
  };

  const deleteTemplate = async (
    template
  ) => {
    const confirmed =
      window.confirm(
        `Delete template "${template.template_name}"?`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    try {
      await Api.deleteDevelopmentDocumentTemplate(
        template.id
      );

      setSuccess(
        "Template deleted successfully."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The template could not be deleted."
      );
    }
  };

  return (
    <div className="development-documents-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Document management
          </p>

          <h2>Documents</h2>

          <p>
            Manage reusable templates and
            generated client, proposal, invoice,
            agreement, and completion documents.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              activeTab === "documents"
                ? openNewDocumentForm
                : openNewTemplateForm
            }
          >
            {activeTab === "documents"
              ? "Add document"
              : "Add template"}
          </button>
        </div>
      </div>

      <div className="development-document-tabs">
        <button
          type="button"
          className={
            activeTab === "documents"
              ? "active"
              : ""
          }
          onClick={() =>
            changeTab("documents")
          }
        >
          Documents
        </button>

        <button
          type="button"
          className={
            activeTab === "templates"
              ? "active"
              : ""
          }
          onClick={() =>
            changeTab("templates")
          }
        >
          Templates
        </button>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {pdfPreviewData && (
        <DevelopmentPdfPreview
          document={
            pdfPreviewData.document
          }
          client={
            pdfPreviewData.client
          }
          project={
            pdfPreviewData.project
          }
          proposal={
            pdfPreviewData.proposal
          }
          invoice={
            pdfPreviewData.invoice
          }
          template={
            pdfPreviewData.template
          }
          items={
            pdfPreviewData.items
          }
          onClose={
            closePdfPreview
          }
          onStored={
            handlePdfStored
          }
        />
      )}

      {emailDocumentData && (
        <EmailDocumentModal
          data={emailDocumentData}
          isSending={
            isEmailingDocument
          }
          feedback={
            emailFeedback
          }
          onChange={(
            field,
            value
          ) =>
            setEmailDocumentData(
              (current) => ({
                ...current,
                [field]: value,
              })
            )
          }
          onClose={() => {
            setEmailDocumentData(
              null
            );

            setEmailFeedback({
              type: "",
              message: "",
            });
          }}
          onSend={
            sendDocumentEmail
          }
        />
      )}

      {emailHistoryData && (
        <EmailHistoryModal
          data={emailHistoryData}
          isLoading={
            isLoadingEmailHistory
          }
          onClose={() =>
            setEmailHistoryData(
              null
            )
          }
        />
      )}

      {activeTab === "documents" &&
        showDocumentForm && (
          <DocumentForm
            formData={documentForm}
            clients={clients}
            projects={availableProjects}
            proposals={
              availableProposals
            }
            invoices={availableInvoices}
            templates={
              availableTemplates
            }
            selectedDocument={
              selectedDocument
            }
            isSaving={isSaving}
            onChange={
              handleDocumentChange
            }
            onSubmit={
              handleDocumentSubmit
            }
            onCancel={
              closeDocumentForm
            }
          />
        )}

      {activeTab === "templates" &&
        showTemplateForm && (
          <TemplateForm
            formData={templateForm}
            selectedTemplate={
              selectedTemplate
            }
            isSaving={isSaving}
            onChange={
              handleTemplateChange
            }
            onSubmit={
              handleTemplateSubmit
            }
            onCancel={
              closeTemplateForm
            }
          />
        )}

      {activeTab === "documents" ? (
        <>
          <section className="development-document-summary-grid">
            <SummaryCard
              label="All documents"
              value={documentSummary.total}
              active={
                statusFilter === "all"
              }
              onClick={() =>
                setStatusFilter("all")
              }
            />

            <SummaryCard
              label="Draft"
              value={documentSummary.draft}
              active={
                statusFilter === "draft"
              }
              onClick={() =>
                setStatusFilter("draft")
              }
            />

            <SummaryCard
              label="Sent"
              value={documentSummary.sent}
              active={
                statusFilter === "sent"
              }
              onClick={() =>
                setStatusFilter("sent")
              }
            />

            <SummaryCard
              label="Finalized"
              value={
                documentSummary.finalized
              }
              active={
                statusFilter ===
                "finalized"
              }
              onClick={() =>
                setStatusFilter(
                  "finalized"
                )
              }
            />

            <SummaryCard
              label="Archived"
              value={
                documentSummary.archived
              }
              active={
                statusFilter ===
                "archived"
              }
              onClick={() =>
                setStatusFilter(
                  "archived"
                )
              }
            />
          </section>

          <DocumentList
            documents={filteredDocuments}
            clients={clientMap}
            projects={projectMap}
            proposals={proposalMap}
            invoices={invoiceMap}
            templates={templateMap}
            search={search}
            typeFilter={typeFilter}
            statusFilter={statusFilter}
            clientFilter={clientFilter}
            clientOptions={clients}
            isLoading={isLoading}
            onSearch={setSearch}
            onTypeFilter={setTypeFilter}
            onStatusFilter={
              setStatusFilter
            }
            onClientFilter={
              setClientFilter
            }
            onEdit={
              openEditDocumentForm
            }
            onMarkSent={
              markDocumentSent
            }
            onFinalize={
              finalizeDocument
            }
            onRestore={
              restoreDocument
            }
            onDelete={deleteDocument}
            onGeneratePdf={
              openPdfPreview
            }
            onViewPdf={(document) =>
              openStoredPdf(
                document,
                false
              )
            }
            onDownloadPdf={(document) =>
              openStoredPdf(
                document,
                true
              )
            }
            onDeletePdf={
              deleteStoredPdf
            }
            onEmail={
              openDocumentEmail
            }
            onEmailHistory={
              openDocumentEmailHistory
            }
            pdfActionDocumentId={
              pdfActionDocumentId
            }
          />
        </>
      ) : (
        <>
          <section className="development-document-summary-grid">
            <SummaryCard
              label="All templates"
              value={templateSummary.total}
              active={
                statusFilter === "all"
              }
              onClick={() =>
                setStatusFilter("all")
              }
            />

            <SummaryCard
              label="Active"
              value={templateSummary.active}
              active={
                statusFilter === "active"
              }
              onClick={() =>
                setStatusFilter("active")
              }
            />

            <SummaryCard
              label="Inactive"
              value={
                templateSummary.inactive
              }
              active={
                statusFilter ===
                "inactive"
              }
              onClick={() =>
                setStatusFilter(
                  "inactive"
                )
              }
            />

            <SummaryCard
              label="Defaults"
              value={
                templateSummary.defaults
              }
              active={
                statusFilter === "default"
              }
              onClick={() =>
                setStatusFilter("default")
              }
            />
          </section>

          <TemplateList
            templates={
              filteredTemplates
            }
            search={search}
            typeFilter={typeFilter}
            statusFilter={statusFilter}
            isLoading={isLoading}
            onSearch={setSearch}
            onTypeFilter={setTypeFilter}
            onStatusFilter={
              setStatusFilter
            }
            onEdit={
              openEditTemplateForm
            }
            onToggleActive={
              toggleTemplateActive
            }
            onToggleDefault={
              toggleTemplateDefault
            }
            onDelete={deleteTemplate}
          />
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-document-summary-card${
        active ? " active" : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function DocumentList({
  documents,
  clients,
  projects,
  proposals,
  invoices,
  templates,
  search,
  typeFilter,
  statusFilter,
  clientFilter,
  clientOptions,
  isLoading,
  onSearch,
  onTypeFilter,
  onStatusFilter,
  onClientFilter,
  onEdit,
  onMarkSent,
  onFinalize,
  onRestore,
  onDelete,
  onGeneratePdf,
  onViewPdf,
  onDownloadPdf,
  onDeletePdf,
  onEmail,
  onEmailHistory,
  pdfActionDocumentId,
}) {
  return (
    <section className="development-documents-panel">
      <div className="development-documents-toolbar">
        <div>
          <h3>Document records</h3>

          <p>
            {documents.length} document
            {documents.length === 1
              ? ""
              : "s"}{" "}
            shown
          </p>
        </div>

        <div className="development-document-filters">
          <input
            type="search"
            value={search}
            onChange={(event) =>
              onSearch(event.target.value)
            }
            placeholder="Search documents..."
          />

          <select
            value={clientFilter}
            onChange={(event) =>
              onClientFilter(
                event.target.value
              )
            }
          >
            <option value="">
              All clients
            </option>

            {clientOptions.map(
              (client) => (
                <option
                  key={client.id}
                  value={client.id}
                >
                  {getClientName(client)}
                </option>
              )
            )}
          </select>

          <select
            value={typeFilter}
            onChange={(event) =>
              onTypeFilter(
                event.target.value
              )
            }
          >
            <option value="">
              All document types
            </option>

            {DOCUMENT_TYPES.map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {formatLabel(type)}
                </option>
              )
            )}
          </select>

          <select
            value={statusFilter}
            onChange={(event) =>
              onStatusFilter(
                event.target.value
              )
            }
          >
            <option value="all">
              All statuses
            </option>

            {DOCUMENT_STATUSES.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {formatLabel(status)}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="development-admin-state">
          Loading documents...
        </div>
      ) : documents.length === 0 ? (
        <div className="development-admin-empty">
          No documents match the current
          filters.
        </div>
      ) : (
        <div className="development-document-card-grid">
          {documents.map((document) => {
            const client =
              clients.get(
                Number(document.client_id)
              );

            const project =
              projects.get(
                Number(document.project_id)
              );

            const proposal =
              proposals.get(
                Number(
                  document.proposal_id
                )
              );

            const invoice =
              invoices.get(
                Number(document.invoice_id)
              );

            const template =
              templates.get(
                Number(document.template_id)
              );

            const sent =
              Number(document.sent) === 1;

            const finalized =
              Number(
                document.finalized
              ) === 1;

            const archived =
              document.status ===
              "archived";

            return (
              <article
                key={document.id}
                className="development-document-card"
              >
                <div className="development-document-card-header">
                  <div>
                    <p>
                      {document.document_number ||
                        `Document ${document.id}`}
                    </p>

                    <h4>
                      {document.title}
                    </h4>

                    <span>
                      {getClientName(client)}
                    </span>
                  </div>

                  <span
                    className={`development-document-status ${document.status}`}
                  >
                    {formatLabel(
                      document.status
                    )}
                  </span>
                </div>

                <div className="development-document-meta-grid">
                  <div>
                    <span>Type</span>

                    <strong>
                      {formatLabel(
                        document.document_type
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Project</span>

                    <strong>
                      {project?.title || "—"}
                    </strong>
                  </div>

                  <div>
                    <span>Proposal</span>

                    <strong>
                      {proposal?.proposal_number ||
                        "—"}
                    </strong>
                  </div>

                  <div>
                    <span>Invoice</span>

                    <strong>
                      {invoice?.invoice_number ||
                        "—"}
                    </strong>
                  </div>

                  <div>
                    <span>Template</span>

                    <strong>
                      {template?.template_name ||
                        "—"}
                    </strong>
                  </div>

                  <div>
                    <span>Updated</span>

                    <strong>
                      {formatDate(
                        document.updated_at
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Sent</span>

                    <strong>
                      {document.sent_date
                        ? formatDateTime(
                            document.sent_date
                          )
                        : "—"}
                    </strong>
                  </div>
                </div>

                <div className="development-document-flags">
                  <span
                    className={
                      sent ? "active" : ""
                    }
                  >
                    {sent ? "✓" : "○"} Sent
                  </span>

                  <span
                    className={
                      finalized
                        ? "active"
                        : ""
                    }
                  >
                    {finalized
                      ? "✓"
                      : "○"}{" "}
                    Finalized
                  </span>
                </div>

                <div className="development-document-card-actions">
                  <button
                    type="button"
                    onClick={() =>
                      onEdit(document)
                    }
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    className="pdf"
                    onClick={() =>
                      onGeneratePdf(
                        document
                      )
                    }
                    disabled={
                      pdfActionDocumentId ===
                      document.id
                    }
                  >
                    {pdfActionDocumentId ===
                    document.id
                      ? "Preparing..."
                      : hasStoredPdf(document)
                        ? "Regenerate PDF"
                        : "Generate PDF"}
                  </button>

                  {hasStoredPdf(document) && (
                    <>
                      <button
                        type="button"
                        className="pdf"
                        onClick={() =>
                          onViewPdf(
                            document
                          )
                        }
                        disabled={
                          pdfActionDocumentId ===
                          document.id
                        }
                      >
                        View PDF
                      </button>

                      <button
                        type="button"
                        className="pdf"
                        onClick={() =>
                          onDownloadPdf(
                            document
                          )
                        }
                        disabled={
                          pdfActionDocumentId ===
                          document.id
                        }
                      >
                        Download
                      </button>

                      <button
                        type="button"
                        className="finalize"
                        onClick={() =>
                          onEmail(
                            document
                          )
                        }
                        disabled={
                          pdfActionDocumentId ===
                          document.id
                        }
                      >
                        Email PDF
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          onEmailHistory(
                            document
                          )
                        }
                        disabled={
                          pdfActionDocumentId ===
                          document.id
                        }
                      >
                        Email history
                      </button>

                      <button
                        type="button"
                        className="danger"
                        onClick={() =>
                          onDeletePdf(
                            document
                          )
                        }
                        disabled={
                          pdfActionDocumentId ===
                          document.id
                        }
                      >
                        Delete PDF
                      </button>
                    </>
                  )}

                  {!archived &&
                    !sent && (
                    <button
                      type="button"
                      onClick={() =>
                        onMarkSent(
                          document
                        )
                      }
                    >
                      Mark sent
                    </button>
                  )}

                  {!archived &&
                    !finalized && (
                    <button
                      type="button"
                      className="finalize"
                      onClick={() =>
                        onFinalize(
                          document
                        )
                      }
                    >
                      Finalize
                    </button>
                  )}

                  {archived && (
                    <button
                      type="button"
                      className="finalize"
                      onClick={() =>
                        onRestore(
                          document
                        )
                      }
                    >
                      Restore Document
                    </button>
                  )}

                  {!archived && (
                    <button
                      type="button"
                      className="danger"
                      onClick={() =>
                        onDelete(document)
                      }
                    >
                      Delete Document
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TemplateList({
  templates,
  search,
  typeFilter,
  statusFilter,
  isLoading,
  onSearch,
  onTypeFilter,
  onStatusFilter,
  onEdit,
  onToggleActive,
  onToggleDefault,
  onDelete,
}) {
  return (
    <section className="development-documents-panel">
      <div className="development-documents-toolbar">
        <div>
          <h3>Document templates</h3>

          <p>
            {templates.length} template
            {templates.length === 1
              ? ""
              : "s"}{" "}
            shown
          </p>
        </div>

        <div className="development-document-filters">
          <input
            type="search"
            value={search}
            onChange={(event) =>
              onSearch(event.target.value)
            }
            placeholder="Search templates..."
          />

          <select
            value={typeFilter}
            onChange={(event) =>
              onTypeFilter(
                event.target.value
              )
            }
          >
            <option value="">
              All template types
            </option>

            {DOCUMENT_TYPES.map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {formatLabel(type)}
                </option>
              )
            )}
          </select>

          <select
            value={statusFilter}
            onChange={(event) =>
              onStatusFilter(
                event.target.value
              )
            }
          >
            <option value="all">
              All templates
            </option>

            <option value="active">
              Active
            </option>

            <option value="inactive">
              Inactive
            </option>

            <option value="default">
              Defaults
            </option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="development-admin-state">
          Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <div className="development-admin-empty">
          No templates match the current
          filters.
        </div>
      ) : (
        <div className="development-template-card-grid">
          {templates.map((template) => {
            const active =
              Number(template.active) === 1;

            const isDefault =
              Number(
                template.is_default
              ) === 1;

            return (
              <article
                key={template.id}
                className={`development-template-card${
                  !active
                    ? " inactive"
                    : ""
                }`}
              >
                <div className="development-template-card-header">
                  <div>
                    <p>
                      {formatLabel(
                        template.document_type
                      )}
                    </p>

                    <h4>
                      {template.template_name}
                    </h4>
                  </div>

                  <div className="development-template-badges">
                    {isDefault && (
                      <span className="default">
                        Default
                      </span>
                    )}

                    <span
                      className={
                        active
                          ? "active"
                          : "inactive"
                      }
                    >
                      {active
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="development-template-preview">
                  <h5>Title format</h5>

                  <p>
                    {template.title_format ||
                      "No title format"}
                  </p>

                  <h5>Body preview</h5>

                  <p>
                    {template.body_content ||
                      "No body content"}
                  </p>
                </div>

                <div className="development-document-card-actions">
                  <button
                    type="button"
                    onClick={() =>
                      onEdit(template)
                    }
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    className={
                      isDefault
                        ? "danger"
                        : "finalize"
                    }
                    onClick={() =>
                      onToggleDefault(
                        template
                      )
                    }
                  >
                    {isDefault
                      ? "Remove default"
                      : "Make default"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      onToggleActive(
                        template
                      )
                    }
                  >
                    {active
                      ? "Deactivate"
                      : "Activate"}
                  </button>

                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      onDelete(template)
                    }
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function DocumentForm({
  formData,
  clients,
  projects,
  proposals,
  invoices,
  templates,
  selectedDocument,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-document-form-panel">
      <div className="development-document-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedDocument
              ? "Edit document"
              : "New document"}
          </p>

          <h3>
            {selectedDocument
              ? selectedDocument.title
              : "Create document"}
          </h3>
        </div>

        <button
          type="button"
          className="development-document-close-button"
          onClick={onCancel}
        >
          ×
        </button>
      </div>

      <form
        className="development-document-form"
        onSubmit={onSubmit}
      >
        <div className="development-document-form-grid">
          <label className="development-document-field">
            <span>Client *</span>

            <select
              name="client_id"
              value={formData.client_id}
              onChange={onChange}
              required
            >
              <option value="">
                Select client
              </option>

              {clients.map((client) => (
                <option
                  key={client.id}
                  value={client.id}
                >
                  {getClientName(client)}
                </option>
              ))}
            </select>
          </label>

          <label className="development-document-field">
            <span>Project</span>

            <select
              name="project_id"
              value={formData.project_id}
              onChange={onChange}
            >
              <option value="">
                No linked project
              </option>

              {projects.map((project) => (
                <option
                  key={project.id}
                  value={project.id}
                >
                  {project.title}
                </option>
              ))}
            </select>
          </label>

          <label className="development-document-field">
            <span>Proposal</span>

            <select
              name="proposal_id"
              value={formData.proposal_id}
              onChange={onChange}
            >
              <option value="">
                No linked proposal
              </option>

              {proposals.map(
                (proposal) => (
                  <option
                    key={proposal.id}
                    value={proposal.id}
                  >
                    {proposal.proposal_number} —{" "}
                    {proposal.title}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-document-field">
            <span>Invoice</span>

            <select
              name="invoice_id"
              value={formData.invoice_id}
              onChange={onChange}
            >
              <option value="">
                No linked invoice
              </option>

              {invoices.map((invoice) => (
                <option
                  key={invoice.id}
                  value={invoice.id}
                >
                  {invoice.invoice_number}
                </option>
              ))}
            </select>
          </label>

          <label className="development-document-field">
            <span>Document type *</span>

            <select
              name="document_type"
              value={
                formData.document_type
              }
              onChange={onChange}
              required
            >
              {DOCUMENT_TYPES.map((type) => (
                <option
                  key={type}
                  value={type}
                >
                  {formatLabel(type)}
                </option>
              ))}
            </select>
          </label>

          <label className="development-document-field">
            <span>Template</span>

            <select
              name="template_id"
              value={formData.template_id}
              onChange={onChange}
            >
              <option value="">
                No template
              </option>

              {templates.map((template) => (
                <option
                  key={template.id}
                  value={template.id}
                >
                  {template.template_name}
                </option>
              ))}
            </select>
          </label>

          <label className="development-document-field full-width">
            <span>Title *</span>

            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={onChange}
              required
            />
          </label>

          {selectedDocument?.document_number && (
            <div className="development-document-readonly-field">
              <span>Document number</span>

              <strong>
                {
                  selectedDocument.document_number
                }
              </strong>
            </div>
          )}

          <label className="development-document-field">
            <span>Status</span>

            <select
              name="status"
              value={formData.status}
              onChange={onChange}
            >
              {DOCUMENT_STATUSES.map(
                (status) => (
                  <option
                    key={status}
                    value={status}
                  >
                    {formatLabel(status)}
                  </option>
                )
              )}
            </select>
          </label>

          {formData.document_type ===
          "agreement" ? (
            <AgreementFields
              details={getAgreementDetails(
                formData.document_details
              )}
              onChange={onChange}
            />
          ) : formData.document_type ===
            "scope_of_work" ? (
            <ScopeOfWorkFields
              details={getScopeOfWorkDetails(
                formData.document_details
              )}
              onChange={onChange}
            />
          ) : formData.document_type === "contract" ? (
            <ContractFields
              details={getContractDetails(formData.document_details)}
              onChange={onChange}
            />
          ) : formData.document_type === "proposal" ? (
            <ProposalDocumentFields
              details={getProposalDocumentDetails(formData.document_details)}
              onChange={onChange}
            />
          ) : formData.document_type === "receipt" ? (
            <ReceiptFields
              details={getReceiptDetails(formData.document_details)}
              onChange={onChange}
            />
          ) : formData.document_type === "completion" ? (
            <CompletionFields
              details={getCompletionDetails(formData.document_details)}
              onChange={onChange}
            />
          ) : formData.document_type === "other" ? (
            <OtherDocumentFields
              details={getOtherDocumentDetails(formData.document_details)}
              onChange={onChange}
            />
          ) : (
            <label className="development-document-field full-width">
              <span>Generated content</span>

              <textarea
                name="generated_content"
                value={
                  formData.generated_content
                }
                onChange={onChange}
                rows="10"
              />
            </label>
          )}

          <label className="development-document-field full-width">
            <span>PDF file URL</span>

            <input
              type="text"
              name="pdf_file_url"
              value={formData.pdf_file_url}
              onChange={onChange}
              placeholder="/api/development/document-pdf/11/view"
            />
          </label>

          <label className="development-document-field full-width">
            <span>Internal notes</span>

            <textarea
              name="internal_notes"
              value={
                formData.internal_notes
              }
              onChange={onChange}
              rows="5"
            />
          </label>
        </div>

        <div className="development-document-checkbox-row">
          <label>
            <input
              type="checkbox"
              name="sent"
              checked={formData.sent}
              onChange={onChange}
            />

            <span>Document sent</span>
          </label>

          <label>
            <input
              type="checkbox"
              name="finalized"
              checked={
                formData.finalized
              }
              onChange={onChange}
            />

            <span>Document finalized</span>
          </label>
        </div>

        <div className="development-document-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedDocument
                ? "Save changes"
                : "Create document"}
          </button>
        </div>
      </form>
    </section>
  );
}

function AgreementFields({
  details,
  onChange,
}) {
  const fieldName = (name) =>
    `document_details.${name}`;

  return (
    <>
      <div className="development-document-type-section full-width">
        <div className="development-document-type-section-heading">
          <div>
            <p className="development-admin-eyebrow">
              Agreement details
            </p>

            <h4>
              Website Development Agreement
            </h4>
          </div>

          <p>
            These sections will be used to
            build the agreement PDF. Every
            field remains editable.
          </p>
        </div>
      </div>

      <label className="development-document-field">
        <span>Effective date *</span>

        <input
          type="date"
          name={fieldName(
            "effective_date"
          )}
          value={safeString(
            details.effective_date
          )}
          onChange={onChange}
          required
        />
      </label>

      <label className="development-document-field">
        <span>Client signer name</span>

        <input
          type="text"
          name={fieldName(
            "client_signer_name"
          )}
          value={safeString(
            details.client_signer_name
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>Client signer title</span>

        <input
          type="text"
          name={fieldName(
            "client_signer_title"
          )}
          value={safeString(
            details.client_signer_title
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>Provider name</span>

        <input
          type="text"
          name={fieldName(
            "provider_name"
          )}
          value={safeString(
            details.provider_name
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field full-width">
        <span>Project scope *</span>

        <textarea
          name={fieldName(
            "project_scope"
          )}
          value={safeString(
            details.project_scope
          )}
          onChange={onChange}
          rows="5"
          required
        />
      </label>

      <label className="development-document-field full-width">
        <span>Payment terms *</span>

        <textarea
          name={fieldName(
            "payment_terms"
          )}
          value={safeString(
            details.payment_terms
          )}
          onChange={onChange}
          rows="4"
          required
        />
      </label>

      <label className="development-document-field full-width">
        <span>Deposit terms</span>

        <textarea
          name={fieldName(
            "deposit_terms"
          )}
          value={safeString(
            details.deposit_terms
          )}
          onChange={onChange}
          rows="4"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Revision allowance</span>

        <textarea
          name={fieldName(
            "revision_allowance"
          )}
          value={safeString(
            details.revision_allowance
          )}
          onChange={onChange}
          rows="4"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Change request terms</span>

        <textarea
          name={fieldName(
            "change_request_terms"
          )}
          value={safeString(
            details.change_request_terms
          )}
          onChange={onChange}
          rows="4"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Client responsibilities *</span>

        <textarea
          name={fieldName(
            "client_responsibilities"
          )}
          value={safeString(
            details.client_responsibilities
          )}
          onChange={onChange}
          rows="5"
          required
        />
      </label>

      <label className="development-document-field full-width">
        <span>Project timeline</span>

        <textarea
          name={fieldName(
            "project_timeline"
          )}
          value={safeString(
            details.project_timeline
          )}
          onChange={onChange}
          rows="4"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Ownership terms</span>

        <textarea
          name={fieldName(
            "ownership_terms"
          )}
          value={safeString(
            details.ownership_terms
          )}
          onChange={onChange}
          rows="5"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Third-party services</span>

        <textarea
          name={fieldName(
            "third_party_services"
          )}
          value={safeString(
            details.third_party_services
          )}
          onChange={onChange}
          rows="4"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Hosting and maintenance</span>

        <textarea
          name={fieldName(
            "hosting_maintenance"
          )}
          value={safeString(
            details.hosting_maintenance
          )}
          onChange={onChange}
          rows="4"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Cancellation terms</span>

        <textarea
          name={fieldName(
            "cancellation_terms"
          )}
          value={safeString(
            details.cancellation_terms
          )}
          onChange={onChange}
          rows="4"
        />
      </label>

      <label className="development-document-field full-width">
        <span>Liability terms</span>

        <textarea
          name={fieldName(
            "liability_terms"
          )}
          value={safeString(
            details.liability_terms
          )}
          onChange={onChange}
          rows="5"
        />
      </label>
    </>
  );
}

function ScopeOfWorkFields({
  details,
  onChange,
}) {
  const fieldName = (name) =>
    `document_details.${name}`;

  const textareaField = (
    label,
    name,
    rows = 4,
    required = false
  ) => (
    <label className="development-document-field full-width">
      <span>
        {label}
        {required ? " *" : ""}
      </span>

      <textarea
        name={fieldName(name)}
        value={safeString(
          details[name]
        )}
        onChange={onChange}
        rows={rows}
        required={required}
      />
    </label>
  );

  return (
    <>
      <div className="development-document-type-section full-width">
        <div className="development-document-type-section-heading">
          <div>
            <p className="development-admin-eyebrow">
              Scope details
            </p>

            <h4>
              Scope of Work
            </h4>
          </div>

          <p>
            These sections define the approved
            work, responsibilities, deliverables,
            timeline, and acceptance requirements.
          </p>
        </div>
      </div>

      <label className="development-document-field">
        <span>Effective date *</span>

        <input
          type="date"
          name={fieldName(
            "effective_date"
          )}
          value={safeString(
            details.effective_date
          )}
          onChange={onChange}
          required
        />
      </label>

      <label className="development-document-field">
        <span>Client approver name</span>

        <input
          type="text"
          name={fieldName(
            "client_approver_name"
          )}
          value={safeString(
            details.client_approver_name
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>Client approver title</span>

        <input
          type="text"
          name={fieldName(
            "client_approver_title"
          )}
          value={safeString(
            details.client_approver_title
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>Provider name</span>

        <input
          type="text"
          name={fieldName(
            "provider_name"
          )}
          value={safeString(
            details.provider_name
          )}
          onChange={onChange}
        />
      </label>

      {textareaField(
        "Project objective",
        "project_objective",
        4,
        true
      )}

      {textareaField(
        "Project overview",
        "project_overview",
        5
      )}

      {textareaField(
        "Deliverables",
        "deliverables",
        6,
        true
      )}

      {textareaField(
        "Included services",
        "included_services",
        5
      )}

      {textareaField(
        "Excluded services",
        "excluded_services",
        5
      )}

      {textareaField(
        "Milestones",
        "milestones",
        5
      )}

      {textareaField(
        "Estimated timeline",
        "estimated_timeline",
        4
      )}

      {textareaField(
        "Client responsibilities",
        "client_responsibilities",
        5,
        true
      )}

      {textareaField(
        "Avwakin Development responsibilities",
        "provider_responsibilities",
        5
      )}

      {textareaField(
        "Required client materials",
        "required_client_materials",
        5
      )}

      {textareaField(
        "Dependencies and assumptions",
        "dependencies_assumptions",
        5
      )}

      {textareaField(
        "Acceptance criteria",
        "acceptance_criteria",
        5
      )}

      {textareaField(
        "Revision allowance",
        "revision_allowance",
        4
      )}

      {textareaField(
        "Change request process",
        "change_request_process",
        5
      )}

      {textareaField(
        "Completion requirements",
        "completion_requirements",
        5
      )}

      {textareaField(
        "Additional terms",
        "additional_terms",
        5
      )}
    </>
  );
}


function ContractFields({
  details,
  onChange,
}) {
  const fieldName = (name) =>
    `document_details.${name}`;

  const textareaFields = [
    ["services", "Services *", 5, true],
    ["compensation", "Compensation *", 4, true],
    ["payment_schedule", "Payment schedule", 4, false],
    ["provider_responsibilities", "Avwakin Development responsibilities", 4, false],
    ["client_responsibilities", "Client responsibilities *", 5, true],
    ["confidentiality", "Confidentiality", 4, false],
    ["intellectual_property", "Intellectual property", 5, false],
    ["third_party_services", "Third-party services", 4, false],
    ["warranties", "Warranties", 4, false],
    ["limitation_of_liability", "Limitation of liability", 5, false],
    ["termination", "Termination", 4, false],
    ["dispute_resolution", "Dispute resolution", 4, false],
    ["governing_law", "Governing law", 3, false],
    ["entire_agreement", "Entire agreement", 4, false],
    ["additional_terms", "Additional terms", 4, false],
  ];

  return (
    <>
      <div className="development-document-type-section full-width">
        <div className="development-document-type-section-heading">
          <div>
            <p className="development-admin-eyebrow">
              Contract details
            </p>

            <h4>
              Development Services Contract
            </h4>
          </div>

          <p>
            These fields create the formal
            contract PDF and remain editable
            before the document is finalized.
          </p>
        </div>
      </div>

      <label className="development-document-field">
        <span>Effective date *</span>

        <input
          type="date"
          name={fieldName(
            "effective_date"
          )}
          value={safeString(
            details.effective_date
          )}
          onChange={onChange}
          required
        />
      </label>

      <label className="development-document-field">
        <span>Start date</span>

        <input
          type="date"
          name={fieldName(
            "start_date"
          )}
          value={safeString(
            details.start_date
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>End date</span>

        <input
          type="date"
          name={fieldName(
            "end_date"
          )}
          value={safeString(
            details.end_date
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>Client signer name</span>

        <input
          type="text"
          name={fieldName(
            "client_signer_name"
          )}
          value={safeString(
            details.client_signer_name
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>Client signer title</span>

        <input
          type="text"
          name={fieldName(
            "client_signer_title"
          )}
          value={safeString(
            details.client_signer_title
          )}
          onChange={onChange}
        />
      </label>

      <label className="development-document-field">
        <span>Provider name</span>

        <input
          type="text"
          name={fieldName(
            "provider_name"
          )}
          value={safeString(
            details.provider_name
          )}
          onChange={onChange}
        />
      </label>

      {textareaFields.map(
        ([
          name,
          label,
          rows,
          required,
        ]) => (
          <label
            key={name}
            className="development-document-field full-width"
          >
            <span>{label}</span>

            <textarea
              name={fieldName(name)}
              value={safeString(
                details[name]
              )}
              onChange={onChange}
              rows={rows}
              required={required}
            />
          </label>
        )
      )}
    </>
  );
}


function DetailInput({ label, name, value, onChange, type = "text", required = false }) {
  return (
    <label className="development-document-field">
      <span>{label}{required ? " *" : ""}</span>
      <input
        type={type}
        name={`document_details.${name}`}
        value={safeString(value)}
        onChange={onChange}
        required={required}
      />
    </label>
  );
}

function DetailTextarea({ label, name, value, onChange, rows = 4, required = false }) {
  return (
    <label className="development-document-field full-width">
      <span>{label}{required ? " *" : ""}</span>
      <textarea
        name={`document_details.${name}`}
        value={safeString(value)}
        onChange={onChange}
        rows={rows}
        required={required}
      />
    </label>
  );
}

function TypeSectionHeading({ eyebrow, title, description }) {
  return (
    <div className="development-document-type-section full-width">
      <div className="development-document-type-section-heading">
        <div>
          <p className="development-admin-eyebrow">{eyebrow}</p>
          <h4>{title}</h4>
        </div>
        <p>{description}</p>
      </div>
    </div>
  );
}

function ProposalDocumentFields({ details, onChange }) {
  return (
    <>
      <TypeSectionHeading
        eyebrow="Proposal document details"
        title="Structured Proposal"
        description="These fields supplement the linked proposal and create a fuller client-facing PDF."
      />
      <DetailTextarea label="Executive summary" name="executive_summary" value={details.executive_summary} onChange={onChange} required />
      <DetailTextarea label="Project objectives" name="project_objectives" value={details.project_objectives} onChange={onChange} />
      <DetailTextarea label="Deliverables" name="deliverables" value={details.deliverables} onChange={onChange} required />
      <DetailTextarea label="Assumptions" name="assumptions" value={details.assumptions} onChange={onChange} />
      <DetailTextarea label="Exclusions" name="exclusions" value={details.exclusions} onChange={onChange} />
      <DetailTextarea label="Client responsibilities" name="client_responsibilities" value={details.client_responsibilities} onChange={onChange} />
      <DetailTextarea label="Avwakin Development responsibilities" name="provider_responsibilities" value={details.provider_responsibilities} onChange={onChange} />
      <DetailTextarea label="Acceptance terms" name="acceptance_terms" value={details.acceptance_terms} onChange={onChange} />
      <DetailTextarea label="Additional terms" name="additional_terms" value={details.additional_terms} onChange={onChange} />
      <DetailInput label="Client approver name" name="client_approver_name" value={details.client_approver_name} onChange={onChange} />
      <DetailInput label="Client approver title" name="client_approver_title" value={details.client_approver_title} onChange={onChange} />
    </>
  );
}

function ReceiptFields({ details, onChange }) {
  return (
    <>
      <TypeSectionHeading
        eyebrow="Receipt details"
        title="Payment Receipt"
        description="Link an invoice above, then enter the payment information that should appear on the receipt."
      />
      <DetailInput label="Payment date" name="payment_date" type="date" value={details.payment_date} onChange={onChange} required />
      <DetailInput label="Amount received" name="amount_received" type="number" value={details.amount_received} onChange={onChange} required />
      <DetailInput label="Payment method" name="payment_method" value={details.payment_method} onChange={onChange} />
      <DetailInput label="Transaction reference" name="transaction_reference" value={details.transaction_reference} onChange={onChange} />
      <DetailInput label="Remaining balance" name="remaining_balance" type="number" value={details.remaining_balance} onChange={onChange} />
      <DetailInput label="Payer name" name="payer_name" value={details.payer_name} onChange={onChange} />
      <DetailInput label="Received by" name="received_by" value={details.received_by} onChange={onChange} />
      <DetailTextarea label="Payment description" name="payment_description" value={details.payment_description} onChange={onChange} />
      <DetailTextarea label="Additional notes" name="additional_notes" value={details.additional_notes} onChange={onChange} />
    </>
  );
}

function CompletionFields({ details, onChange }) {
  return (
    <>
      <TypeSectionHeading
        eyebrow="Completion details"
        title="Project Completion Document"
        description="Record the delivered work, handoff information, remaining items, and client acceptance."
      />
      <DetailInput label="Completion date" name="completion_date" type="date" value={details.completion_date} onChange={onChange} required />
      <DetailInput label="Client approver name" name="client_approver_name" value={details.client_approver_name} onChange={onChange} />
      <DetailInput label="Client approver title" name="client_approver_title" value={details.client_approver_title} onChange={onChange} />
      <DetailTextarea label="Completion summary" name="completion_summary" value={details.completion_summary} onChange={onChange} />
      <DetailTextarea label="Delivered items" name="delivered_items" value={details.delivered_items} onChange={onChange} required />
      <DetailTextarea label="Outstanding items" name="outstanding_items" value={details.outstanding_items} onChange={onChange} />
      <DetailTextarea label="Credentials transferred" name="credentials_transferred" value={details.credentials_transferred} onChange={onChange} />
      <DetailTextarea label="Training provided" name="training_provided" value={details.training_provided} onChange={onChange} />
      <DetailTextarea label="Support period" name="support_period" value={details.support_period} onChange={onChange} />
      <DetailTextarea label="Warranty period" name="warranty_period" value={details.warranty_period} onChange={onChange} />
      <DetailTextarea label="Final balance terms" name="final_balance_terms" value={details.final_balance_terms} onChange={onChange} />
      <DetailTextarea label="Client acceptance" name="client_acceptance" value={details.client_acceptance} onChange={onChange} />
      <DetailTextarea label="Additional notes" name="additional_notes" value={details.additional_notes} onChange={onChange} />
    </>
  );
}

function OtherDocumentFields({ details, onChange }) {
  return (
    <>
      <TypeSectionHeading
        eyebrow="Custom document details"
        title="Other Document"
        description="Use these fields for a structured client document that does not match another document type."
      />
      <DetailInput label="Effective date" name="effective_date" type="date" value={details.effective_date} onChange={onChange} />
      <DetailInput label="Prepared by" name="prepared_by" value={details.prepared_by} onChange={onChange} />
      <DetailInput label="Prepared for" name="prepared_for" value={details.prepared_for} onChange={onChange} />
      <DetailTextarea label="Purpose" name="purpose" value={details.purpose} onChange={onChange} />
      <DetailTextarea label="Summary" name="summary" value={details.summary} onChange={onChange} />
      <DetailTextarea label="Document body" name="document_body" value={details.document_body} onChange={onChange} rows={10} required />
      <DetailTextarea label="Action items" name="action_items" value={details.action_items} onChange={onChange} />
      <DetailTextarea label="References" name="references" value={details.references} onChange={onChange} />
      <DetailTextarea label="Additional notes" name="additional_notes" value={details.additional_notes} onChange={onChange} />
    </>
  );
}

function EmailDocumentModal({
  data,
  isSending,
  feedback,
  onChange,
  onClose,
  onSend,
}) {
  return (
    <div
      className="development-pdf-modal-backdrop"
      role="presentation"
    >
      <section
        className="development-pdf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="development-document-email-title"
      >
        <div className="development-pdf-modal-heading">
          <div>
            <p className="development-admin-eyebrow">
              Email document
            </p>

            <h3 id="development-document-email-title">
              {data.document.title}
            </h3>

            <span>
              The stored PDF will be
              attached automatically.
            </span>
          </div>

          <button
            type="button"
            className="development-pdf-close-button"
            onClick={onClose}
            disabled={isSending}
            aria-label="Close email document form"
          >
            ×
          </button>
        </div>

        <form
          className="development-document-form"
          onSubmit={onSend}
        >
          {feedback?.message && (
            <div
              className={`development-admin-alert ${
                feedback.type === "error"
                  ? "error"
                  : feedback.type === "success"
                    ? "success"
                    : ""
              }`}
              role={
                feedback.type === "error"
                  ? "alert"
                  : "status"
              }
            >
              {feedback.message}
            </div>
          )}
          <div className="development-document-form-grid">
            <label className="development-document-field full-width">
              <span>
                Recipient email *
              </span>

              <input
                type="email"
                value={data.to}
                onChange={(event) =>
                  onChange(
                    "to",
                    event.target.value
                  )
                }
                required
              />
            </label>

            <label className="development-document-field full-width">
              <span>
                Subject *
              </span>

              <input
                type="text"
                value={data.subject}
                onChange={(event) =>
                  onChange(
                    "subject",
                    event.target.value
                  )
                }
                required
              />
            </label>

            <label className="development-document-field full-width">
              <span>
                Message *
              </span>

              <textarea
                value={data.message}
                onChange={(event) =>
                  onChange(
                    "message",
                    event.target.value
                  )
                }
                rows="10"
                required
              />
            </label>
          </div>

          <div className="development-pdf-modal-actions">
            <button
              type="button"
              className="development-admin-button secondary"
              onClick={onClose}
              disabled={isSending}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="development-admin-button primary"
              disabled={isSending}
            >
              {isSending
                ? "Sending..."
                : "Send PDF"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function EmailHistoryModal({
  data,
  isLoading,
  onClose,
}) {
  return (
    <div
      className="development-pdf-modal-backdrop"
      role="presentation"
    >
      <section
        className="development-pdf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="development-document-email-history-title"
      >
        <div className="development-pdf-modal-heading">
          <div>
            <p className="development-admin-eyebrow">
              Email history
            </p>

            <h3 id="development-document-email-history-title">
              {data.document.title}
            </h3>

            <span>
              Previous delivery attempts for
              this document.
            </span>
          </div>

          <button
            type="button"
            className="development-pdf-close-button"
            onClick={onClose}
            aria-label="Close email history"
          >
            ×
          </button>
        </div>

        <div className="development-document-form">
          {isLoading ? (
            <div className="development-admin-state">
              Loading email history...
            </div>
          ) : data.history.length === 0 ? (
            <div className="development-admin-empty">
              No email history has been recorded
              for this document.
            </div>
          ) : (
            <div className="development-document-card-grid">
              {data.history.map(
                (entry) => (
                  <article
                    key={entry.id}
                    className="development-document-card"
                  >
                    <div className="development-document-card-header">
                      <div>
                        <p>
                          {formatDateTime(
                            entry.sent_at ||
                              entry.completed_at ||
                              entry.created_at
                          )}
                        </p>

                        <h4>
                          {entry.subject}
                        </h4>

                        <span>
                          {entry.recipient_email}
                        </span>
                      </div>

                      <span
                        className={`development-document-status ${
                          entry.status === "sent"
                            ? "sent"
                            : entry.status === "failed"
                              ? "cancelled"
                              : "draft"
                        }`}
                      >
                        {formatLabel(
                          entry.status
                        )}
                      </span>
                    </div>

                    <div className="development-document-meta-grid">
                      <div>
                        <span>Attachment</span>

                        <strong>
                          {entry.attachment_file_name ||
                            "—"}
                        </strong>
                      </div>

                      <div>
                        <span>Completed</span>

                        <strong>
                          {formatDateTime(
                            entry.completed_at
                          )}
                        </strong>
                      </div>
                    </div>

                    {entry.message_text && (
                      <p>
                        {entry.message_text}
                      </p>
                    )}

                    {entry.error_message && (
                      <div
                        className="development-admin-alert error"
                        role="alert"
                      >
                        {entry.error_message}
                      </div>
                    )}
                  </article>
                )
              )}
            </div>
          )}
        </div>

        <div className="development-pdf-modal-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </section>
    </div>
  );
}

function TemplateForm({
  formData,
  selectedTemplate,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-document-form-panel">
      <div className="development-document-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedTemplate
              ? "Edit template"
              : "New template"}
          </p>

          <h3>
            {selectedTemplate
              ? selectedTemplate.template_name
              : "Create template"}
          </h3>
        </div>

        <button
          type="button"
          className="development-document-close-button"
          onClick={onCancel}
        >
          ×
        </button>
      </div>

      <form
        className="development-document-form"
        onSubmit={onSubmit}
      >
        <div className="development-document-form-grid">
          <label className="development-document-field">
            <span>Template name *</span>

            <input
              type="text"
              name="template_name"
              value={
                formData.template_name
              }
              onChange={onChange}
              required
            />
          </label>

          <label className="development-document-field">
            <span>Document type *</span>

            <select
              name="document_type"
              value={
                formData.document_type
              }
              onChange={onChange}
              required
            >
              {DOCUMENT_TYPES.map((type) => (
                <option
                  key={type}
                  value={type}
                >
                  {formatLabel(type)}
                </option>
              ))}
            </select>
          </label>

          <label className="development-document-field full-width">
            <span>Title format</span>

            <input
              type="text"
              name="title_format"
              value={
                formData.title_format
              }
              onChange={onChange}
              placeholder="Proposal for {{client_name}}"
            />
          </label>

          <label className="development-document-field full-width">
            <span>Header content</span>

            <textarea
              name="header_content"
              value={
                formData.header_content
              }
              onChange={onChange}
              rows="4"
            />
          </label>

          <label className="development-document-field full-width">
            <span>Body content *</span>

            <textarea
              name="body_content"
              value={
                formData.body_content
              }
              onChange={onChange}
              rows="12"
              required
            />
          </label>

          <label className="development-document-field full-width">
            <span>Footer content</span>

            <textarea
              name="footer_content"
              value={
                formData.footer_content
              }
              onChange={onChange}
              rows="4"
            />
          </label>

          <label className="development-document-field full-width">
            <span>Default terms</span>

            <textarea
              name="default_terms"
              value={
                formData.default_terms
              }
              onChange={onChange}
              rows="6"
            />
          </label>
        </div>

        <div className="development-document-checkbox-row">
          <label>
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={onChange}
            />

            <span>Template active</span>
          </label>

          <label>
            <input
              type="checkbox"
              name="is_default"
              checked={
                formData.is_default
              }
              onChange={onChange}
            />

            <span>Default template</span>
          </label>
        </div>

        <div className="development-document-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedTemplate
                ? "Save changes"
                : "Create template"}
          </button>
        </div>
      </form>
    </section>
  );
}

export default DevelopmentDocuments;